import {
  WIZARD_STEPS,
  type IdealDesignDto,
  type IdealItemDto,
  type IdealRhythm,
  type StepGuideDto,
  type WizardStepKey,
} from '@bk/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTenantQuery } from '../../../lib/useTenant';
import { useSetupProgress } from '../Setup';
import { RoleStandards } from './RoleStandards';

/**
 * Il lavoro del ruolo (ADR 0137): toccando un ruolo, i processi in cui lavora (le fasi delle attività che svolge, sede
 * per sede), le procedure che svolge e quelle di cui risponde, e i programmi con cui lavora con i prodotti veri più
 * probabili per l'azienda. Tutto viene dai passi già progettati, letti senza farli progettare: un passo che la persona
 * non ha ancora raggiunto non si apre e la scheda lo dice in grigio.
 */

const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** Una voce di un passo con il posto in cui sta: l'impresa (sede null), una sede, un'attività di una sede. */
interface Placed {
  v: IdealItemDto;
  sede: string | null;
  att: string | null;
}

function placed(d: IdealDesignDto | null | undefined): Placed[] {
  if (!d) return [];
  return [
    ...(d.impresa?.voci ?? []).map((v) => ({ v, sede: null, att: null })),
    ...d.sedi.flatMap((s) => [
      ...(s.sede_in_se?.voci ?? []).map((v) => ({ v, sede: s.sede, att: null })),
      ...s.attivita.flatMap((a) => a.voci.map((v) => ({ v, sede: s.sede, att: a.attivita }))),
    ]),
  ];
}

export interface RoleProcess {
  attivita: string;
  sede: string;
  /** Le fasi dell'attività nell'ordine del passo Attività, con quelle che il ruolo svolge. */
  fasi: Array<{ nome: string; sua: boolean }>;
}
export interface RoleProcedure {
  v: IdealItemDto;
  sede: string | null;
  att: string | null;
  risponde: boolean;
  svolge: boolean;
}
export interface RoleTool {
  nome: string;
  risponde: boolean;
  v: IdealItemDto | null;
}

/**
 * Il lavoro di un ruolo dai passi progettati. `sedi` sono le sedi in cui il ruolo c'è (null = tutta l'impresa): un
 * ruolo dell'impresa guarda tutte le sedi, uno di sede solo le sue e ciò che vale per tutta l'impresa.
 */
export function roleWork(
  role: { nome: string; sedi: Array<string | null>; fasi: string[]; strumenti: Array<{ nome: string; risponde: boolean }> },
  steps: { attivita?: IdealDesignDto | null; procedure?: IdealDesignDto | null; software?: IdealDesignDto | null },
): { processi: RoleProcess[]; altre_fasi: string[]; procedure: RoleProcedure[]; programmi: RoleTool[] } {
  const everywhere = role.sedi.includes(null);
  const inScope = (sede: string | null) => everywhere || sede === null || role.sedi.includes(sede);
  const mine = new Set(role.fasi.map(norm));
  const seen = new Set<string>();
  const processi: RoleProcess[] = [];
  for (const s of steps.attivita?.sedi ?? []) {
    if (!inScope(s.sede)) continue;
    for (const a of s.attivita) {
      const fasi = a.voci.filter((v) => v.gruppo?.toLowerCase() === 'fasi').map((v) => v.nome);
      const hit = fasi.filter((f) => mine.has(norm(f)));
      if (!hit.length) continue;
      hit.forEach((f) => seen.add(norm(f)));
      processi.push({ attivita: a.attivita, sede: s.sede, fasi: fasi.map((nome) => ({ nome, sua: mine.has(norm(nome)) })) });
    }
  }
  const altre_fasi = role.fasi.filter((f, i) => !seen.has(norm(f)) && role.fasi.findIndex((g) => norm(g) === norm(f)) === i);
  const me = norm(role.nome);
  const procedure = placed(steps.procedure)
    .filter((p) => inScope(p.sede))
    .map((p) => ({
      ...p,
      risponde: !!p.v.riporta_a && norm(p.v.riporta_a) === me,
      svolge: (p.v.svolge ?? []).some((n) => norm(n) === me),
    }))
    .filter((p) => p.risponde || p.svolge);
  const tools = new Map(placed(steps.software).map((p) => [norm(p.v.nome), p.v]));
  // Lo stesso programma scritto in modi diversi (sedi diverse, maiuscole) è uno solo; ne risponde se ne risponde in una.
  const mineTools = new Map<string, { nome: string; risponde: boolean }>();
  for (const x of role.strumenti) {
    const k = norm(x.nome);
    const was = mineTools.get(k);
    mineTools.set(k, { nome: tools.get(k)?.nome ?? was?.nome ?? x.nome, risponde: !!was?.risponde || x.risponde });
  }
  const programmi = [...mineTools].map(([k, x]) => ({ ...x, v: tools.get(k) ?? null }));
  return { processi, altre_fasi, procedure, programmi };
}

const RHYTHM: Record<IdealRhythm, string> = {
  giorno: 'ogni giorno',
  settimana: 'ogni settimana',
  mese: 'ogni mese',
  trimestre: 'ogni trimestre',
  semestre: 'ogni semestre',
  anno: 'ogni anno',
  evento: 'quando serve',
  continuo: 'sempre aperta',
};
const ORDER: IdealRhythm[] = ['giorno', 'settimana', 'mese', 'trimestre', 'semestre', 'anno', 'evento', 'continuo'];

/** Un passo si legge solo quando la persona ci è arrivata: aprirlo prima lo farebbe progettare in anticipo. */
function useStep(step: WizardStepKey, raggiunto: WizardStepKey | undefined) {
  const reached = !!raggiunto && WIZARD_STEPS.indexOf(raggiunto) >= WIZARD_STEPS.indexOf(step);
  const q = useTenantQuery<StepGuideDto>(['guide', step], `/onboarding/guide/${step}`, { enabled: reached });
  return { reached, design: q.data?.ideale ?? null, loading: reached && q.isLoading };
}

export function RoleWork({
  nome,
  sedi,
  v,
  natura,
}: {
  nome: string;
  sedi: Array<string | null>;
  v: IdealItemDto;
  natura: string;
}) {
  const progress = useSetupProgress();
  const raggiunto = progress.data?.guidata.raggiunto;
  const att = useStep('attivita', raggiunto);
  const sw = useStep('software', raggiunto);
  const proc = useStep('procedure', raggiunto);
  if (natura === 'organo') return null;
  const w = roleWork(
    { nome, sedi, fasi: v.fasi ?? [], strumenti: v.strumenti ?? [] },
    { attivita: att.design, procedure: proc.design, software: sw.design },
  );
  const procs = [...w.procedure].sort((a, b) => ORDER.indexOf(a.v.ritmo ?? 'continuo') - ORDER.indexOf(b.v.ritmo ?? 'continuo'));
  const multi = new Set(w.processi.map((p) => p.sede)).size > 1 || new Set(procs.map((p) => p.sede ?? '')).size > 1;
  return (
    <div className="mt-3 rounded-xl bg-[#f7f8fb] px-3 py-3 ring-1 ring-black/5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Il lavoro del ruolo</p>
      <div className="mt-2 space-y-4">
        <Section k="Processi in cui lavora" n={w.processi.length}>
          {w.processi.length ? (
            <ul className="space-y-2">
              {w.processi.map((p) => (
                <li key={`${p.sede}|${p.attivita}`}>
                  <p className="text-sm font-medium">
                    {p.attivita}
                    {multi && <span className="font-normal text-ink/50"> · {p.sede}</span>}
                  </p>
                  {/* Le fasi del processo in ordine: in evidenza quelle che svolge il ruolo. */}
                  <ol className="mt-1 flex flex-wrap items-center gap-1 text-xs">
                    {p.fasi.map((f, i) => (
                      <li key={f.nome} className="flex items-center gap-1">
                        {i > 0 && <span className="text-ink/25">→</span>}
                        <span className={f.sua ? 'rounded-md bg-accent/10 px-1.5 py-0.5 font-medium text-accent' : 'text-ink/45'}>
                          {f.nome}
                        </span>
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ul>
          ) : null}
          {w.altre_fasi.length > 0 && (
            <p className="mt-1 text-sm">
              {w.processi.length ? 'Anche: ' : ''}
              {w.altre_fasi.join(' · ')}
            </p>
          )}
          {!w.processi.length && !w.altre_fasi.length && (
            <Grey>{att.loading ? 'Leggo le attività…' : 'Nessuna fase delle attività è affidata a questo ruolo.'}</Grey>
          )}
        </Section>

        <Section k="Procedure" n={procs.length}>
          {!proc.reached ? (
            <Grey>Le procedure del ruolo compaiono qui quando arrivi al passo Procedure.</Grey>
          ) : proc.loading || (!proc.design && proc.reached) ? (
            <Grey>Leggo le procedure…</Grey>
          ) : procs.length ? (
            <ul className="divide-y divide-black/5">
              {procs.map((p) => (
                <Procedure key={`${p.sede}|${p.att}|${p.v.nome}`} p={p} multi={multi} />
              ))}
            </ul>
          ) : (
            <Grey>Nessuna procedura dice che la svolge o ne risponde questo ruolo.</Grey>
          )}
        </Section>

        <Section k="Programmi con cui lavora" n={w.programmi.length}>
          {w.programmi.length ? (
            <ul className="space-y-2">
              {w.programmi.map((t) => (
                <Tool key={t.nome} t={t} />
              ))}
            </ul>
          ) : (
            <Grey>Nessun programma dell’azienda ideale è affidato a questo ruolo.</Grey>
          )}
          {sw.reached && w.programmi.some((t) => t.v?.diffusi?.length) && (
            <p className="mt-1 text-[11px] text-ink/40">
              I prodotti sono i più probabili per un’azienda come la vostra, trovati dal cervello con le fonti: un
              riferimento, non un consiglio.
            </p>
          )}
        </Section>
      </div>
    </div>
  );
}

function Procedure({ p, multi }: { p: RoleProcedure; multi: boolean }) {
  const [open, setOpen] = useState(false);
  const how = p.risponde && p.svolge ? 'la svolge e ne risponde' : p.risponde ? 'ne risponde' : 'la svolge';
  const others = p.v.svolge ?? [];
  return (
    <li className="py-1.5">
      <button className="flex w-full items-start gap-2 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{p.v.nome}</span>
          <span className="block text-xs text-ink/50">
            {[how, p.v.ritmo ? RHYTHM[p.v.ritmo] : p.v.quantita, multi ? (p.sede ?? 'tutta l’impresa') : null, p.att]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
        {p.v.obbligatorio && p.v.norma && (
          <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">{p.v.norma}</span>
        )}
        <span className="shrink-0 text-ink/30">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5 pl-1 text-sm">
          {p.v.descrizione && <p>{p.v.descrizione}</p>}
          {p.v.dettagli.length > 0 && (
            <ol className="list-decimal pl-5">
              {p.v.dettagli.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ol>
          )}
          <p className="text-xs text-ink/55">
            {[
              p.v.riporta_a && `Ne risponde: ${p.v.riporta_a}`,
              others.length ? `La svolgono: ${others.join(', ')}` : null,
              p.v.strumenti?.length ? `Con: ${p.v.strumenti.map((s) => s.nome).join(', ')}` : null,
              p.v.prova && `Lascia: ${p.v.prova}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      )}
    </li>
  );
}

function Tool({ t }: { t: RoleTool }) {
  const products = t.v?.usate ? [] : (t.v?.diffusi ?? []).slice(0, 3);
  return (
    <li>
      <p className="text-sm">
        <span className="font-medium">{t.nome}</span>
        {t.risponde && <span className="text-ink/50"> · ne risponde</span>}
      </p>
      {t.v?.usate && <p className="text-xs text-ink/60">Usate già {t.v.usate}</p>}
      {t.v?.servizio && <p className="text-xs text-ink/60">Con il servizio pubblico {t.v.servizio}</p>}
      {products.length > 0 && (
        <p className="text-xs text-ink/60">
          Probabili:{' '}
          {products.map((x, i) => (
            <span key={x.nome}>
              {i > 0 && ' · '}
              <a href={x.fonte} target="_blank" rel="noreferrer" title={x.perche ?? undefined} className="underline decoration-black/15 hover:text-accent">
                {x.nome}
              </a>
              {x.produttore && !x.nome.toLowerCase().includes(x.produttore.toLowerCase()) && (
                <span className="text-ink/40"> ({x.produttore})</span>
              )}
            </span>
          ))}
        </p>
      )}
    </li>
  );
}

function Section({ k, n, children }: { k: string; n: number; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-ink/40">
        {k}
        {n > 0 && <span className="ml-1 text-ink/30">{n}</span>}
      </p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Grey({ children }: { children: ReactNode }) {
  return <p className="text-xs text-ink/40">{children}</p>;
}

/**
 * La scheda del ruolo fuori dall'organigramma (ADR 0137): dove un ruolo compare per nome (il pannello del reparto nella
 * pianta del finale), toccandolo si apre di lato la stessa scheda: mansioni, lavoro del ruolo e standard. Il ruolo si
 * cerca per nome nell'organigramma progettato, preferendo la stessa sede; tutte le sue sedi sommano fasi e strumenti.
 */
export function RoleSheet({ nome, sede, onClose }: { nome: string; sede: string | null; onClose: () => void }) {
  const org = useTenantQuery<StepGuideDto>(['guide', 'organizzazione'], '/onboarding/guide/organizzazione');
  const all = placed(org.data?.ideale).filter((p) => norm(p.v.nome) === norm(nome));
  const s = all.find((p) => p.sede === sede) ?? all.find((p) => p.sede === null) ?? all[0];
  const site = org.data?.ideale?.sedi.find((x) => x.sede === (s?.sede ?? sede)) ?? org.data?.ideale?.sedi[0];
  const place = (site?.luogo ?? '').split(',').map((x) => x.trim()).filter(Boolean);
  const uniq = <T,>(xs: T[]) => [...new Set(xs)];
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  // Sopra tutta la pagina: dentro una scena animata un elemento fisso resterebbe chiuso nel suo riquadro.
  return createPortal(
    <>
      <div className="fixed inset-0 z-40 bg-ink/35" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="ruolo-titolo"
        className="fixed inset-y-0 right-0 z-50 w-full max-w-[560px] overflow-y-auto bg-white px-5 py-5 text-ink shadow-2xl sm:px-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-ink/45">
              {[s?.v.gruppo, s?.att, uniq(all.map((x) => x.sede ?? 'tutta l’impresa')).join(', ')].filter(Boolean).join(' · ')}
            </p>
            <h3 id="ruolo-titolo" className="mt-1 text-xl font-semibold leading-tight">
              {s?.v.nome ?? nome}
            </h3>
            {s?.v.riporta_a && <p className="text-xs text-ink/55">risponde a {s.v.riporta_a}</p>}
          </div>
          <button onClick={onClose} aria-label="Chiudi" className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/[0.05] text-lg">
            ×
          </button>
        </div>
        {!s ? (
          <p className="mt-4 text-sm text-ink/50">{org.isLoading ? 'Leggo l’organigramma…' : 'Questo ruolo non è nell’organigramma progettato.'}</p>
        ) : (
          <>
            {s.v.descrizione && <p className="mt-3 text-[15px] leading-normal">{s.v.descrizione}</p>}
            {s.v.dettagli.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wide text-ink/40">Mansioni</p>
                <ul className="mt-1 list-disc pl-4 text-sm">
                  {s.v.dettagli.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
            {s.v.quantita && (
              <p className="mt-2 text-sm">
                <span className="text-ink/50">Quante persone: </span>
                {s.v.quantita}
              </p>
            )}
            <RoleWork
              nome={s.v.nome}
              sedi={uniq(all.map((x) => x.sede))}
              v={{
                ...s.v,
                fasi: uniq(all.flatMap((x) => x.v.fasi ?? [])),
                strumenti: all.flatMap((x) => x.v.strumenti ?? []),
              }}
              natura={s.v.natura ?? 'ruolo'}
            />
            <RoleStandards
              v={s.v}
              others={placed(org.data?.ideale).map((x) => ({ nome: x.v.nome, codice: x.v.professione?.codice ?? null }))}
              regione={place.at(-1) ?? null}
              provincia={place.at(-2) ?? null}
            />
            {s.v.perche && (
              <p className="mt-3 text-sm">
                <span className="text-[11px] uppercase tracking-wide text-ink/40">Perché c’è nella tua azienda</span>
                <span className="block">{s.v.perche}</span>
              </p>
            )}
          </>
        )}
      </aside>
    </>,
    document.body,
  );
}
